import { useId } from 'react'
import { createPortal } from 'react-dom'
import DatePicker from 'react-datepicker'
import { Form, InputGroup } from 'react-bootstrap'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCalendarDays } from '@fortawesome/free-solid-svg-icons'
import 'react-datepicker/dist/react-datepicker.css'

export function todayDateKey() {
  return dateKey(new Date())
}

export function dueDateIso(value: string) {
  const date = parseValue(value)
  if (!value.includes('T')) date.setHours(9, 0, 0, 0)
  return date.toISOString()
}

export function DueDateField({
  label = 'Due',
  value,
  onChange,
  required = false,
}: {
  label?: string
  value: string
  onChange: (value: string) => void
  required?: boolean
}) {
  const switchId = useId()
  const showTime = value.includes('T')
  const selected = parseValue(value)
  const earliest = earliestDate()

  function commit(date: Date, timed = showTime) {
    const next = clamp(date, timed, earliest)
    onChange(timed ? toValue(next) : dateKey(next))
  }

  return (
    <Form.Group className="mb-3 due-date">
      <Form.Label>{label}</Form.Label>
      <InputGroup>
        <InputGroup.Text>
          <FontAwesomeIcon icon={faCalendarDays} />
        </InputGroup.Text>
        <DatePicker
          selected={selected}
          onChange={(date: Date | null) => { if (date) commit(date) }}
          showTimeInput={showTime}
          timeInputLabel="Time"
          customTimeInput={<TimeEntry />}
          dateFormat={showTime ? 'dd-MM-yyyy HH:mm' : 'dd-MM-yyyy'}
          minDate={startOfToday()}
          className="form-control"
          popperClassName="due-date-popper"
          popperContainer={Popper}
          todayButton="Today"
          shouldCloseOnSelect={!showTime}
        />
      </InputGroup>
      <Form.Check
        type="switch"
        className="mt-2"
        id={switchId}
        label="include time"
        checked={showTime}
        onChange={(event) => commit(selected, event.target.checked)}
      />
      <input type="hidden" name="due" value={showTime ? toValue(clamp(selected, true, earliest)) : dateKey(selected)} required={required} />
    </Form.Group>
  )
}

function TimeEntry({
  value,
  onChange,
  date,
}: {
  value?: string
  onChange?: (time: string) => void
  date?: Date
}) {
  const earliest = earliestDate()
  const min = date && dateKey(date) === dateKey(earliest) ? timeKey(earliest) : undefined
  return (
    <input
      type="time"
      className="react-datepicker-time__input"
      value={value ?? ''}
      min={min}
      onChange={(event) => {
        const next = event.target.value
        if (!next) return
        onChange?.(min && next < min ? min : next)
      }}
    />
  )
}

function Popper({ children }: { children?: React.ReactNode }) {
  return createPortal(children, document.body)
}

function startOfToday() {
  const date = new Date()
  date.setHours(0, 0, 0, 0)
  return date
}

function earliestDate() {
  const next = new Date(Date.now() + 60_000)
  next.setSeconds(0, 0)
  return next
}

function parseValue(value: string) {
  const [year, month, day] = value.slice(0, 10).split('-').map(Number)
  const date = new Date(year || 1970, (month || 1) - 1, day || 1, 9, 0, 0, 0)
  if (value.includes('T')) {
    const [hour, minute] = value.slice(11, 16).split(':').map(Number)
    date.setHours(hour || 0, minute || 0, 0, 0)
  }
  return date
}

function dateKey(date: Date) {
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
}

function timeKey(date: Date) {
  return `${pad(date.getHours())}:${pad(date.getMinutes())}`
}

function toValue(date: Date) {
  return `${dateKey(date)}T${timeKey(date)}`
}

function clamp(date: Date, timed: boolean, earliest: Date) {
  const today = startOfToday()
  const next = new Date(date)
  if (next < today) return timed && earliest > today ? earliest : today
  if (timed && next < earliest) return earliest
  return next
}

function pad(value: number) {
  return String(value).padStart(2, '0')
}
